import {
  type InferOutput,
  array,
  boolean,
  enum_,
  nullish,
  number,
  object,
  optional,
  picklist,
  string,
} from "valibot"

import { PricingPlanTypes } from "@finq/pricing/types"

const OccupationSchema = object({
  id: number(),
  descriptionHe: string(),
  descriptionEn: string(),
})

const CountryResidentSchema = object({
  id: string(),
  descriptionHe: string(),
  descriptionEn: string(),
  countryCode: string(),
})

const BankAccountSchema = object({
  accountNumber: string(),
  bankNumber: number(),
  nameHe: string(),
  nameEn: string(),
  logo: string(),
  branchNumber: number(),
  branchNameHe: string(),
  branchNameEn: string(),
})

const SubscriptionSchema = object({
  productId: picklist(Object.values(PricingProductEnum)),
  planCode: picklist(Object.values(PricingPlanTypes.PlanCode)),
  productPlanPricingType: picklist(Object.values(PricingPlanTypes.PlanPeriodType)),
})

export type UserSubscription = InferOutput<typeof SubscriptionSchema>

export const UserDetailsSchema = object({
  investmentRiskLevel: number(),
  ...userDetailsSchema.entries,
})

export type UserDetails = InferOutput<typeof UserDetailsSchema>

export const UserSchema = object({
  id: string(),
  firstName: string(),
  lastName: string(),
  email: string(),
  genderPronoun: nullish(picklist(["F", "M"])),
  gender: picklist(["F", "M"]),
  hash: optional(string()),
  waitingListNotifications: optional(array(enum_(UpcomingProdCodesEnum))),
  dateOfBirth: number(),
  defaultLanguage: string(),
  city: nullish(string()),
  street: nullish(string()),
  profileProgress: number(),
  idCardNumber: string(),
  idCardIssueDate: number(),
  emailProposal: boolean(),
  emailConfirmed: boolean(),
  publicRole: number(),
  userStatus: nullish(enum_(UserStatusEnum)),
  picture: nullish(string()),
  lastSeen: nullish(number()),
  updateDate: nullish(number()),
  finqRiskLevel: nullish(number()),
  finqRiskLevelUpdateDate: nullish(number()),
  moneyOrigin: nullish(string()),
  hasContractDocuments: nullish(boolean()),
  byPost: nullish(boolean()),
  cell: nullish(string()),
  postalCode: nullish(string()),
  homeNumber: nullish(number()),
  investAmount: nullish(string()),
  signedToc: nullish(boolean()),
  signedMarketing: nullish(boolean()),
  signedBeneficiaryStatement: nullish(boolean()),
  beneficiaryStatementSignedDate: nullish(number()),
  beneficiaryStatement: nullish(number()),
  occupation: nullish(OccupationSchema),
  approvedContract: nullish(boolean()),
  allowedProducts: nullish(array(string())),
  otherCountryResident: nullish(boolean()),
  linkToAuth0Id: nullish(string()),
  usResident: nullish(boolean()),
  bankAccountExist: nullish(boolean()),
  countryResident: nullish(CountryResidentSchema),
  bankAccount: nullish(BankAccountSchema),
  // Auth
  accessToken: nullish(string()),
  refreshToken: nullish(string()),
  expiresIn: nullish(number()),
  loginTimestamp: nullish(number()),

  subscriptions: optional(array(SubscriptionSchema), []),
  waitingList: optional(array(SubscriptionSchema), []),
  userDetails: nullish(UserDetailsSchema),
})

export type User = InferOutput<typeof UserSchema>

export const UserMeetingSchema = object({
  externalEventId: string(),
  status: picklist(["active", "canceled"]),
  startTime: number(),
  createdAt: number(),
})

export type UserMeeting = InferOutput<typeof UserMeetingSchema>
